import type { CSSProperties } from 'react';
import type {
  PanfletoContentConfig,
  PanfletoContentStyles,
  PanfletoDraggableElementId,
  PanfletoElementPlacement,
  PanfletoElementPlacements,
  PanfletoFieldStyle,
  PanfletoProductNameAlign,
  PanfletoStyleFieldKey,
  PanfletoTextOuterShadowDirection,
  Promotion,
  LayoutStyle,
  TextStyle,
} from '../../../shared/types';

/**
 * Cor hex usável em CSS/inputs (#rrggbb). Aceita com ou sem #, 3 ou 6 hex; vazio = inválido.
 */
export function normalizeHexColorString(input: string | undefined | null): string {
  const s = (input ?? '').trim();
  if (!s) return '';
  const withHash = s.startsWith('#') ? s : `#${s}`;
  const body = withHash.slice(1);
  if (/^[0-9A-Fa-f]{3}$/.test(body)) {
    return `#${body[0]}${body[0]}${body[1]}${body[1]}${body[2]}${body[2]}`.toLowerCase();
  }
  if (/^[0-9A-Fa-f]{6}$/.test(body)) {
    return `#${body}`.toLowerCase();
  }
  if (/^[0-9A-Fa-f]{8}$/.test(body)) {
    return `#${body.slice(0, 6)}`.toLowerCase();
  }
  return '';
}

/**
 * Cor de fundo do panfleto vinda de JSON (string #hex, "ffff00", número 0xRRGGBB, etc.) — nunca perder o valor
 * "quase" hex por causa do formato.
 */
/** Cor padrão do palco panfleto — alinhada ao input de cor do editor (`#ffffff`). */
export const PANFLETO_DEFAULT_BACKGROUND_COLOR = '#ffffff';

/**
 * Cor de fundo do panfleto pronta para `backgroundColor` (Display, TV, StylePreview, PromoCard).
 * Alinha com o fluxo do `productContainer.backgroundColor` nas colunas: usa merge + hex/rgb/hsl.
 * Independente das cores da borda compartilhada (painel texto / produto / traço).
 * No palco pode ficar sob a imagem de fundo (opacidade/blur).
 */
export function getPanfletoBackgroundCssColor(
  content: PanfletoContentConfig | undefined | null
): string {
  const m = mergePanfletoContent(content ?? undefined);
  const raw = m.backgroundColor;
  if (raw == null) return PANFLETO_DEFAULT_BACKGROUND_COLOR;
  const s = String(raw).trim();
  if (!s) return PANFLETO_DEFAULT_BACKGROUND_COLOR;
  const hex = normalizeHexColorString(s);
  if (hex) return hex;
  if (/^rgb/i.test(s) || /^hsl/i.test(s)) return s;
  return s;
}

/** Indica se a cor de fundo foi definida explicitamente no JSON (não só o padrão branco). */
export function panfletoHasExplicitBackgroundColor(
  content: PanfletoContentConfig | undefined | null
): boolean {
  const raw = content?.backgroundColor;
  return raw != null && String(raw).trim() !== '';
}

function coercePanfletoBackgroundToCss(input: unknown): string {
  if (input === undefined || input === null) return '';
  if (typeof input === 'number' && !Number.isNaN(input) && input >= 0) {
    const n = Math.trunc(input) & 0xffffff;
    return `#${n.toString(16).padStart(6, '0')}`;
  }
  const s0 = String(input).trim();
  if (!s0) return '';
  if (/^rgb/i.test(s0) || /^hsl/i.test(s0)) {
    return s0;
  }
  if (/^\d+$/.test(s0)) {
    const n = parseInt(s0, 10);
    if (!Number.isNaN(n) && n >= 0) {
      const t = n > 0xffffff ? n & 0xffffff : n;
      return `#${(t & 0xffffff).toString(16).padStart(6, '0')}`;
    }
  }
  const norm = normalizeHexColorString(s0);
  if (norm) return norm;
  return s0;
}

export const PANFLETO_STYLE_FIELD_LABELS: Record<PanfletoStyleFieldKey, string> = {
  texto1: 'Texto 1',
  texto2: 'Texto 2',
  texto3: 'Texto 3',
  linha1: 'Nome do produto',
  linha2: 'Categoria do produto',
  precoDe: 'Preço "De"',
  precoPor: 'Preço "Por"',
  validade: 'Validade',
  quantidade: 'Quantidade e unidade',
};

const STYLE_FIELD_LAYOUT_KEY: Record<
  PanfletoStyleFieldKey,
  keyof Pick<LayoutStyle, 'description' | 'productName' | 'category' | 'price' | 'unit' | 'validUntil'>
> = {
  texto1: 'description',
  texto2: 'description',
  texto3: 'description',
  linha1: 'productName',
  linha2: 'category',
  precoDe: 'price',
  precoPor: 'price',
  validade: 'validUntil',
  quantidade: 'unit',
};

/** Cor e tamanho padrão vindos do LayoutStyle quando não há override em panfletoContent.styles. */
export function getPanfletoFieldBase(layoutStyle: LayoutStyle | undefined, field: PanfletoStyleFieldKey) {
  const lk = STYLE_FIELD_LAYOUT_KEY[field];
  const ts = layoutStyle?.[lk] as TextStyle | undefined;
  const defaultColor = field === 'validade' ? '#cc0000' : '#111111';
  const defaultSize = field === 'validade' ? 14 : 16;
  return {
    color: ts?.color ?? defaultColor,
    fontSize: ts?.fontSize ?? defaultSize,
  };
}

/**
 * Efeito visual da “borda interna” dos campos de texto no panfleto (sólida ou gradiente).
 * Gradiente: anel via `border` transparente + dupla camada de `background` (área interna transparente).
 */
export function buildPanfletoTextFieldBorderEffectStyle(
  border: NonNullable<PanfletoFieldStyle['border']>
): Pick<
  CSSProperties,
  | 'display'
  | 'backgroundColor'
  | 'backgroundImage'
  | 'backgroundOrigin'
  | 'backgroundClip'
  | 'border'
  | 'overflow'
  | 'outline'
  | 'borderRadius'
  | 'padding'
> {
  const rawW = Math.max(0, Number(border.width ?? 0));
  /** Borda ativa com espessura 0 (JSON legado) não desenha; mínimo 1px quando `enabled`. */
  const borderWidth = border.enabled === true ? Math.max(1, rawW) : rawW;
  const borderRadius = Math.max(0, Number(border.radius ?? 0));
  const solidColor = normalizeHexColorString(String(border.color || '').trim()) || String(border.color || '').trim() || '#000000';
  const useGradient = border.useGradient === true;
  const g0 = String(border.gradientStart ?? '').trim();
  const g1 = String(border.gradientEnd ?? '').trim();
  const gStart =
    normalizeHexColorString(g0) || (g0.toLowerCase().startsWith('rgb') ? g0 : '') || '#7c3aed';
  const gEnd =
    normalizeHexColorString(g1) || (g1.toLowerCase().startsWith('rgb') ? g1 : '') || '#fbbf24';
  const angleRaw = Number(border.gradientAngleDeg);
  const angle = Number.isFinite(angleRaw) ? Math.max(0, Math.min(360, angleRaw)) : 135;

  const base: Pick<
    CSSProperties,
    'display' | 'overflow' | 'outline' | 'borderRadius' | 'padding' | 'border'
  > = {
    display: 'inline-block',
    overflow: 'hidden',
    outline: 'none',
    border: 'none',
    borderRadius: borderRadius > 0 ? `${borderRadius}px` : undefined,
    padding: undefined,
  };

  if (useGradient && borderWidth > 0) {
    return {
      ...base,
      backgroundColor: undefined,
      backgroundImage: `linear-gradient(rgba(0,0,0,0), rgba(0,0,0,0)), linear-gradient(${angle}deg, ${gStart}, ${gEnd})`,
      backgroundOrigin: 'padding-box, border-box',
      backgroundClip: 'padding-box, border-box',
      border: `${borderWidth}px solid transparent`,
      padding: 0,
    };
  }

  return {
    ...base,
    backgroundColor: solidColor,
    backgroundClip: 'padding-box',
    padding: borderWidth > 0 ? `${borderWidth}px` : undefined,
  };
}

const PANFLETO_TEXT_SHADOW_VECTOR: Record<
  PanfletoTextOuterShadowDirection,
  readonly [number, number]
> = {
  down: [0, 1],
  up: [0, -1],
  right: [1, 0],
  left: [-1, 0],
};

function normalizePanfletoTextOuterShadowDirection(
  d: string | undefined
): PanfletoTextOuterShadowDirection {
  if (d === 'up' || d === 'down' || d === 'left' || d === 'right') return d;
  return 'down';
}

/** Valor de `text-shadow` para sombra externa no texto do panfleto. */
export function buildPanfletoTextOuterShadowCss(
  outer: PanfletoFieldStyle['textOuterShadow'] | undefined
): string | undefined {
  if (!outer || outer.enabled !== true) return undefined;
  const n = Math.max(1, Math.min(48, Math.round(Number(outer.sizePx) || 4)));
  const dir = normalizePanfletoTextOuterShadowDirection(outer.direction);
  const [vx, vy] = PANFLETO_TEXT_SHADOW_VECTOR[dir];
  const dist = Math.max(1, Math.round(n * 0.38));
  const ox = vx * dist;
  const oy = vy * dist;
  const blur = n;
  const dist2 = Math.round(dist * 1.45);
  const ox2 = vx * dist2;
  const oy2 = vy * dist2;
  const softBlur = Math.round(n * 0.65);
  return `${ox}px ${oy}px ${blur}px rgba(0,0,0,0.82), ${ox2}px ${oy2}px ${softBlur}px rgba(0,0,0,0.36)`;
}

/** Mescla estilo base (ex.: getTextStyle) com overrides do panfletoContent.styles[field]. */
export function mergePanfletoFieldStyle(
  base: CSSProperties,
  field: PanfletoStyleFieldKey,
  content: PanfletoContentConfig | undefined
): CSSProperties {
  const c = mergePanfletoContent(content);
  const ov = c.styles?.[field];
  const out: CSSProperties = { ...base };
  if (ov?.color) out.color = ov.color;
  // fontSize=0 é usado como "ocultar campo"; em renderizações que ainda usem o estilo,
  // manter 1px evita aparência quebrada.
  if (ov?.fontSize !== undefined && ov.fontSize >= 0) {
    out.fontSize = ov.fontSize === 0 ? '1px' : `${ov.fontSize}px`;
  }
  if (ov?.border?.enabled) {
    const fx = buildPanfletoTextFieldBorderEffectStyle(ov.border);
    Object.assign(out, fx);
  }
  const shadowCss = buildPanfletoTextOuterShadowCss(ov?.textOuterShadow);
  if (shadowCss) out.textShadow = shadowCss;
  return out;
}

export type PanfletoPricePairPart = 'label' | 'value';

/** Valores válidos de `styles.linha1.textAlign` no JSON. */
export function normalizePanfletoProductNameAlign(raw: unknown): PanfletoProductNameAlign | undefined {
  const s = String(raw ?? '').trim().toLowerCase();
  if (s === 'left' || s === 'center' || s === 'right') return s;
  return undefined;
}

function readProductNameAlignFromField(field?: PanfletoFieldStyle): PanfletoProductNameAlign | undefined {
  if (!field) return undefined;
  const extra = field as PanfletoFieldStyle & { TextAlign?: unknown };
  return normalizePanfletoProductNameAlign(field.textAlign ?? extra.TextAlign);
}

/**
 * Alinhamento efetivo do nome do produto. JSON antigo sem o campo continua `left`.
 * Não escreve o campo no JSON até o usuário escolher no editor.
 */
export function resolvePanfletoProductNameAlign(
  content: PanfletoContentConfig | undefined
): PanfletoProductNameAlign {
  return readProductNameAlignFromField(content?.styles?.linha1) ?? 'left';
}

/**
 * Patch parcial de `linha1` (cor, borda, tamanho) não envia `textAlign`.
 * Mantém o valor anterior salvo no JSON; só troca se o next trouxer left/center/right.
 */
export function preservePanfletoProductNameAlign(
  prev: PanfletoFieldStyle | undefined,
  next: PanfletoFieldStyle | undefined
): PanfletoFieldStyle | undefined {
  const fromNext = readProductNameAlignFromField(next);
  const fromPrev = readProductNameAlignFromField(prev);
  const kept = fromNext ?? fromPrev;
  if (!next && !kept) return next;
  const out: PanfletoFieldStyle = { ...(next ?? {}) };
  delete (out as { TextAlign?: unknown }).TextAlign;
  if (kept) out.textAlign = kept;
  else delete (out as { textAlign?: PanfletoProductNameAlign }).textAlign;
  return Object.keys(out).length > 0 ? out : undefined;
}

/** JSON/API podem enviar boolean ou string `"true"`. */
export function isPanfletoPriceSplitOn(ov: PanfletoFieldStyle | undefined): boolean {
  const v = ov?.priceSplitCustomize;
  if (v === true) return true;
  return String(v ?? '').toLowerCase() === 'true';
}

/**
 * Estilo do rótulo ("DE"/"POR") ou do valor numérico em `precoDe` / `precoPor`.
 * Com `priceSplitCustomize`, cores e tamanhos vêm dos campos dedicados; sem isso, `color`/`fontSize` só no valor.
 */
export function mergePanfletoPricePairStyle(
  base: CSSProperties,
  field: 'precoDe' | 'precoPor',
  part: PanfletoPricePairPart,
  content: PanfletoContentConfig | undefined
): CSSProperties {
  const c = mergePanfletoContent(content);
  const ov = c.styles?.[field];
  const out: CSSProperties = { ...base };
  const split = isPanfletoPriceSplitOn(ov);

  if (split && ov) {
    if (part === 'label') {
      const raw = ov.priceLabelColor ?? ov.color;
      if (raw !== undefined && String(raw).trim() !== '') {
        const s = String(raw);
        out.color = normalizeHexColorString(s) || s.trim();
      }
      if (ov.priceLabelFontSize !== undefined && ov.priceLabelFontSize >= 0) {
        out.fontSize = ov.priceLabelFontSize === 0 ? '1px' : `${ov.priceLabelFontSize}px`;
      }
    } else {
      const raw = ov.priceValueColor ?? ov.color;
      if (raw !== undefined && String(raw).trim() !== '') {
        const s = String(raw);
        out.color = normalizeHexColorString(s) || s.trim();
      }
      if (ov.priceValueFontSize !== undefined && ov.priceValueFontSize >= 0) {
        out.fontSize = ov.priceValueFontSize === 0 ? '1px' : `${ov.priceValueFontSize}px`;
      } else if (ov.fontSize !== undefined && ov.fontSize >= 0) {
        out.fontSize = ov.fontSize === 0 ? '1px' : `${ov.fontSize}px`;
      }
    }
  } else {
    if (ov?.color) out.color = ov.color;
    if (part === 'value' && ov?.fontSize !== undefined && ov.fontSize >= 0) {
      out.fontSize = ov.fontSize === 0 ? '1px' : `${ov.fontSize}px`;
    }
  }

  const shadowCss = buildPanfletoTextOuterShadowCss(ov?.textOuterShadow);
  if (shadowCss) out.textShadow = shadowCss;
  return out;
}

/** Campo oculto no panfleto quando o override salva fontSize=0. */
export function panfletoFieldIsHidden(
  content: PanfletoContentConfig | undefined,
  field: PanfletoStyleFieldKey
): boolean {
  const c = mergePanfletoContent(content);
  return c.styles?.[field]?.fontSize === 0;
}

export const PANFLETO_DRAGGABLE_ELEMENT_IDS: PanfletoDraggableElementId[] = [
  'texto1',
  'texto2',
  'texto3',
  'linha1',
  'linha2',
  'imagemProduto',
  'precoDe',
  'precoPorLabel',
  'precoPorValor',
  'quantidade',
  'validade',
];

export const PANFLETO_IMAGEM_MIN_WIDTH_PX = 120;
export const PANFLETO_IMAGEM_MIN_HEIGHT_PX = 300;
/** Compat legado: manter para usos antigos que esperam um único mínimo. */
export const PANFLETO_IMAGEM_MIN_PX = PANFLETO_IMAGEM_MIN_HEIGHT_PX;

function clampPct(n: number): number {
  if (Number.isNaN(n)) return 0;
  return Math.min(100, Math.max(0, n));
}

function normalizeLegacyPlacementKeys(
  src?: PanfletoElementPlacements | null
): PanfletoElementPlacements | undefined {
  if (!src) return undefined;
  const out: PanfletoElementPlacements = { ...src };
  const legacyCabecalho = (src as Record<string, PanfletoElementPlacement | undefined>).cabecalho;
  const legacyLinha3 = (src as Record<string, PanfletoElementPlacement | undefined>).linha3;
  if (!out.texto1 && legacyCabecalho) out.texto1 = { ...legacyCabecalho };
  if (!out.texto3 && legacyLinha3) out.texto3 = { ...legacyLinha3 };
  delete (out as Record<string, unknown>).cabecalho;
  delete (out as Record<string, unknown>).linha3;
  return out;
}

export function mergePanfletoElementPlacements(
  prev?: PanfletoElementPlacements | null,
  next?: PanfletoElementPlacements | null
): PanfletoElementPlacements | undefined {
  const prevNorm = normalizeLegacyPlacementKeys(prev);
  const nextNorm = normalizeLegacyPlacementKeys(next);
  if (!prevNorm && !nextNorm) return undefined;
  const out: PanfletoElementPlacements = {};
  for (const id of PANFLETO_DRAGGABLE_ELEMENT_IDS) {
    const a = prevNorm?.[id];
    const b = nextNorm?.[id];
    if (!a && !b) continue;
    const xPct = clampPct(b?.xPct ?? a?.xPct ?? 50);
    const yPct = clampPct(b?.yPct ?? a?.yPct ?? 50);
    const placement: PanfletoElementPlacement = { xPct, yPct };
    if (id === 'imagemProduto') {
      const w = b?.widthPx ?? a?.widthPx;
      const h = b?.heightPx ?? a?.heightPx;
      if (w !== undefined) placement.widthPx = Math.max(PANFLETO_IMAGEM_MIN_WIDTH_PX, w);
      if (h !== undefined) placement.heightPx = Math.max(PANFLETO_IMAGEM_MIN_HEIGHT_PX, h);
    }
    out[id] = placement;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/** Layout livre ativo quando há ao menos uma posição salva. */
export function panfletoHasFreeformPlacements(content: PanfletoContentConfig | undefined | null): boolean {
  const pl = mergePanfletoContent(content ?? undefined).elementPlacements;
  return Boolean(pl && Object.keys(pl).length > 0);
}

/**
 * Posições iniciais ao entrar no modo edição sem JSON prévio (pilha vertical equilibrada).
 * Nome do produto (`linha1`): xPct = borda esquerda. Demais textos e imagem: xPct = centro.
 */
export function getDefaultPanfletoElementPlacements(): PanfletoElementPlacements {
  return {
    texto1: { xPct: 50, yPct: 12 },
    texto2: { xPct: 50, yPct: 19 },
    texto3: { xPct: 50, yPct: 26 },
    linha1: { xPct: 4, yPct: 35 },
    linha2: { xPct: 50, yPct: 43 },
    imagemProduto: { xPct: 50, yPct: 55, widthPx: 380, heightPx: 380 },
    precoDe: { xPct: 50, yPct: 70 },
    precoPorLabel: { xPct: 50, yPct: 77 },
    precoPorValor: { xPct: 50, yPct: 83 },
    quantidade: { xPct: 50, yPct: 90 },
    validade: { xPct: 50, yPct: 96 },
  };
}

/**
 * Posições iniciais alinhadas ao split texto | foto (borda compartilhada).
 * Usado ao passar de 0 → ≥1 produtos na tela panfleto para encaixar no painel correto.
 * Nome do produto (`linha1`): xPct = borda esquerda do painel de texto.
 */
export function getDefaultPanfletoElementPlacementsForProductShowcase(): PanfletoElementPlacements {
  return {
    texto1: { xPct: 24, yPct: 12 },
    texto2: { xPct: 24, yPct: 20 },
    texto3: { xPct: 24, yPct: 28 },
    linha1: { xPct: 3, yPct: 37 },
    linha2: { xPct: 24, yPct: 46 },
    imagemProduto: { xPct: 75, yPct: 50, widthPx: 420, heightPx: 420 },
    precoDe: { xPct: 24, yPct: 70 },
    precoPorLabel: { xPct: 24, yPct: 76 },
    precoPorValor: { xPct: 24, yPct: 80 },
    quantidade: { xPct: 24, yPct: 88 },
    validade: { xPct: 24, yPct: 94 },
  };
}

/**
 * Normaliza um único `PanfletoFieldStyle` já mesclado (ex.: prev + patch).
 * Usado em `mergePanfletoStyles` e no editor de estilo para não perder `border` definida no modo posição.
 */
export function normalizePanfletoFieldStyleRecord(merged: PanfletoFieldStyle): PanfletoFieldStyle {
  const cleaned: PanfletoFieldStyle = {};
  if (merged.color !== undefined && String(merged.color).trim() !== '') {
    const c = String(merged.color);
    const norm = normalizeHexColorString(c);
    cleaned.color = norm || c.trim();
  }
  if (merged.fontSize !== undefined && merged.fontSize >= 0) cleaned.fontSize = merged.fontSize;
  if (merged.border !== undefined) {
    const borderEnabled = merged.border?.enabled === true;
    const borderColorRaw = String(merged.border?.color ?? '').trim();
    const borderColorNormalized = normalizeHexColorString(borderColorRaw) || borderColorRaw;
    const borderWidth = Number(merged.border?.width ?? 0);
    const borderRadius = Number(merged.border?.radius ?? 0);
    const useGradient = merged.border?.useGradient === true;
    const gs = String(merged.border?.gradientStart ?? '').trim();
    const ge = String(merged.border?.gradientEnd ?? '').trim();
    const ga = Number(merged.border?.gradientAngleDeg);
    if (
      borderEnabled ||
      !!borderColorNormalized ||
      Number.isFinite(borderWidth) ||
      Number.isFinite(borderRadius) ||
      useGradient ||
      !!gs ||
      !!ge ||
      Number.isFinite(ga)
    ) {
      const widthNorm = Number.isFinite(borderWidth) ? borderWidth : 0;
      const borderOut: NonNullable<PanfletoFieldStyle['border']> = {
        enabled: borderEnabled,
        color: borderColorNormalized || '#000000',
        width: borderEnabled ? Math.max(1, widthNorm) : Math.max(0, widthNorm),
        radius: Number.isFinite(borderRadius) ? Math.max(0, borderRadius) : 0,
      };
      if (useGradient) {
        borderOut.useGradient = true;
        const ngs = normalizeHexColorString(gs) || (gs.toLowerCase().startsWith('rgb') ? gs : '');
        const nge = normalizeHexColorString(ge) || (ge.toLowerCase().startsWith('rgb') ? ge : '');
        if (ngs) borderOut.gradientStart = ngs;
        else if (gs) borderOut.gradientStart = gs;
        if (nge) borderOut.gradientEnd = nge;
        else if (ge) borderOut.gradientEnd = ge;
        if (Number.isFinite(ga)) {
          borderOut.gradientAngleDeg = Math.max(0, Math.min(360, ga));
        }
      }
      cleaned.border = borderOut;
    }
  }
  if (merged.textOuterShadow !== undefined) {
    const shadowOn = merged.textOuterShadow.enabled === true;
    const rawSize = Number(merged.textOuterShadow.sizePx);
    const sizePx = Number.isFinite(rawSize) ? Math.max(1, Math.min(48, Math.round(rawSize))) : 4;
    const direction = normalizePanfletoTextOuterShadowDirection(merged.textOuterShadow.direction);
    if (shadowOn) {
      cleaned.textOuterShadow = { enabled: true, sizePx, direction };
    } else if (merged.textOuterShadow.enabled === false) {
      // Explícito off (ex.: colar estilo sem sombra) — não deixar o merge reaproveitar sombra antiga.
      cleaned.textOuterShadow = { enabled: false, sizePx, direction };
    }
  }
  if (isPanfletoPriceSplitOn(merged)) {
    cleaned.priceSplitCustomize = true;
    cleaned.priceCaptionLayout = merged.priceCaptionLayout === 'left' ? 'left' : 'above';
    if (merged.priceLabelColor !== undefined && String(merged.priceLabelColor).trim() !== '') {
      const s = String(merged.priceLabelColor);
      cleaned.priceLabelColor = normalizeHexColorString(s) || s.trim();
    }
    if (merged.priceValueColor !== undefined && String(merged.priceValueColor).trim() !== '') {
      const s = String(merged.priceValueColor);
      cleaned.priceValueColor = normalizeHexColorString(s) || s.trim();
    }
    if (merged.priceLabelFontSize !== undefined && merged.priceLabelFontSize >= 0) {
      cleaned.priceLabelFontSize = Math.min(200, Math.round(merged.priceLabelFontSize));
    }
    if (merged.priceValueFontSize !== undefined && merged.priceValueFontSize >= 0) {
      cleaned.priceValueFontSize = Math.min(200, Math.round(merged.priceValueFontSize));
    }
  } else if (merged.priceSplitCustomize === false) {
    cleaned.priceSplitCustomize = false;
  }
  const textAlign = readProductNameAlignFromField(merged);
  if (textAlign) cleaned.textAlign = textAlign;
  return cleaned;
}

/**
 * Snapshot visual de um campo do panfleto (cor, tamanho, borda, sombra, split de preço, alinhamento).
 * Não inclui o texto/conteúdo — só aparência. Usa base do LayoutStyle quando o override não tem cor/tamanho.
 */
export function snapshotPanfletoFieldVisualStyle(
  layoutStyle: LayoutStyle | undefined,
  field: PanfletoStyleFieldKey,
  stored?: PanfletoFieldStyle | null
): PanfletoFieldStyle {
  const base = getPanfletoFieldBase(layoutStyle, field);
  const src = stored ?? {};
  const draft: PanfletoFieldStyle = {
    color: src.color ?? base.color,
    fontSize: src.fontSize !== undefined ? src.fontSize : base.fontSize,
  };
  if (src.border) {
    draft.border = { ...src.border };
  }
  if (src.textOuterShadow) {
    draft.textOuterShadow = { ...src.textOuterShadow };
  }
  if (isPanfletoPriceSplitOn(src)) {
    draft.priceSplitCustomize = true;
    draft.priceCaptionLayout = src.priceCaptionLayout === 'left' ? 'left' : 'above';
    if (src.priceLabelColor !== undefined) draft.priceLabelColor = src.priceLabelColor;
    if (src.priceValueColor !== undefined) draft.priceValueColor = src.priceValueColor;
    if (src.priceLabelFontSize !== undefined) draft.priceLabelFontSize = src.priceLabelFontSize;
    if (src.priceValueFontSize !== undefined) draft.priceValueFontSize = src.priceValueFontSize;
  }
  const align = readProductNameAlignFromField(src);
  if (align) draft.textAlign = align;
  return normalizePanfletoFieldStyleRecord(draft);
}

/**
 * Adapta um snapshot visual para colar em outro campo.
 * `textAlign` só vai para `linha1`; split DE/POR só para `precoDe`/`precoPor`.
 * Ausência de borda/sombra no origem vira desligado explícito (para o replace no save limpar o destino).
 */
export function adaptPanfletoFieldVisualStyleForTarget(
  snapshot: PanfletoFieldStyle,
  targetField: PanfletoStyleFieldKey
): PanfletoFieldStyle {
  const out: PanfletoFieldStyle = {
    color: snapshot.color,
    fontSize: snapshot.fontSize,
  };

  if (snapshot.border?.enabled === true) {
    out.border = { ...snapshot.border, enabled: true };
  } else {
    out.border = { enabled: false, color: '#000000', width: 0, radius: 0 };
  }

  if (snapshot.textOuterShadow?.enabled === true) {
    out.textOuterShadow = { ...snapshot.textOuterShadow, enabled: true };
  } else {
    out.textOuterShadow = { enabled: false, sizePx: 4, direction: 'down' };
  }

  if (targetField === 'linha1') {
    const align = readProductNameAlignFromField(snapshot);
    if (align) out.textAlign = align;
  }

  if (targetField === 'precoDe' || targetField === 'precoPor') {
    if (isPanfletoPriceSplitOn(snapshot)) {
      out.priceSplitCustomize = true;
      out.priceCaptionLayout = snapshot.priceCaptionLayout === 'left' ? 'left' : 'above';
      if (snapshot.priceLabelColor !== undefined) out.priceLabelColor = snapshot.priceLabelColor;
      if (snapshot.priceValueColor !== undefined) out.priceValueColor = snapshot.priceValueColor;
      if (snapshot.priceLabelFontSize !== undefined) out.priceLabelFontSize = snapshot.priceLabelFontSize;
      if (snapshot.priceValueFontSize !== undefined) out.priceValueFontSize = snapshot.priceValueFontSize;
    } else {
      out.priceSplitCustomize = false;
    }
  }

  return normalizePanfletoFieldStyleRecord(out);
}

export function mergePanfletoStyles(
  prev?: PanfletoContentStyles,
  next?: PanfletoContentStyles
): PanfletoContentStyles | undefined {
  const normalize = (s?: PanfletoContentStyles) => {
    if (!s) return undefined;
    const out = { ...s } as Record<string, PanfletoFieldStyle>;
    if (!out.texto1 && out.cabecalho) out.texto1 = { ...out.cabecalho };
    if (!out.texto3 && out.linha3) out.texto3 = { ...out.linha3 };
    delete out.cabecalho;
    delete out.linha3;
    return out as PanfletoContentStyles;
  };
  const prevNorm = normalize(prev);
  const nextNorm = normalize(next);
  if (!prevNorm && !nextNorm) return undefined;
  const keys: PanfletoStyleFieldKey[] = [
    'texto1',
    'texto2',
    'texto3',
    'linha1',
    'linha2',
    'precoDe',
    'precoPor',
    'validade',
    'quantidade',
  ];
  const out: PanfletoContentStyles = {};
  for (const k of keys) {
    let merged: PanfletoFieldStyle = { ...prevNorm?.[k], ...nextNorm?.[k] };
    if (k === 'linha1') {
      const preserved = preservePanfletoProductNameAlign(prevNorm?.[k], merged);
      if (preserved) merged = preserved;
    }
    const cleaned = normalizePanfletoFieldStyleRecord(merged);
    if (Object.keys(cleaned).length > 0) out[k] = cleaned;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

export function defaultPanfletoContent(): PanfletoContentConfig {
  return {
    texto1: '',
    texto2: '',
    texto3: '',
    validadeModo: 'ate',
    productShowcase: {
      sharedFrameScale: 1,
      panelGapPx: 0,
      sharedBorderWidth: 2,
      sharedBorderRadius: 26,
      sharedBorderColor: '#f1f5f9',
      textPanelBgColor: '#ef4444',
      productPanelBgColor: '#f8fafc',
    },
  };
}

export type MergePanfletoContentOptions = {
  /** false = serialização para PUT (não injeta textos/showcase padrão). Default true (UI/load). */
  includeDefaults?: boolean;
};

export function mergePanfletoContent(
  partial?: PanfletoContentConfig | null,
  options?: MergePanfletoContentOptions
): PanfletoContentConfig {
  const withDefaults = options?.includeDefaults !== false;
  const base = withDefaults ? defaultPanfletoContent() : {};
  if (!partial) return withDefaults ? (base as PanfletoContentConfig) : {};
  const { styles: incomingStyles, elementPlacements: incomingPlacements, ...rest0 } = partial;
  const rest = { ...rest0 } as Record<string, unknown>;
  delete rest.linha1;
  delete rest.linha2;
  delete rest.codigoBarras;
  delete rest.tipoQuantidade;
  const spread: PanfletoContentConfig = { ...base, ...(rest as Partial<PanfletoContentConfig>) };
  if (!spread.texto1 && spread.cabecalho) {
    spread.texto1 = String(spread.cabecalho);
  }
  if (!spread.texto3 && spread.linha3) {
    spread.texto3 = String(spread.linha3);
  }
  delete (spread as { cabecalho?: string }).cabecalho;
  delete (spread as { linha3?: string }).linha3;
  delete (spread as { tipoQuantidade?: string }).tipoQuantidade;
  if (spread.backgroundColor !== undefined && String(spread.backgroundColor).trim() === '') {
    delete (spread as { backgroundColor?: string }).backgroundColor;
  } else if (spread.backgroundColor !== undefined && String(spread.backgroundColor).trim() !== '') {
    (spread as { backgroundColor?: string }).backgroundColor = coercePanfletoBackgroundToCss(
      spread.backgroundColor
    );
  }
  const mergedPlacements = mergePanfletoElementPlacements(
    spread.elementPlacements as PanfletoElementPlacements | undefined,
    incomingPlacements as PanfletoElementPlacements | undefined
  );
  if (mergedPlacements && Object.keys(mergedPlacements).length > 0) {
    spread.elementPlacements = mergedPlacements;
  } else {
    delete (spread as { elementPlacements?: PanfletoElementPlacements }).elementPlacements;
  }
  let mergedStyles = mergePanfletoStyles(spread.styles, incomingStyles);
  const keptNameAlign =
    readProductNameAlignFromField(mergedStyles?.linha1) ??
    readProductNameAlignFromField(incomingStyles?.linha1);
  if (keptNameAlign) {
    mergedStyles = {
      ...(mergedStyles ?? {}),
      linha1: { ...(mergedStyles?.linha1 ?? {}), textAlign: keptNameAlign },
    };
  }
  const showcase = spread.productShowcase;
  if (showcase && typeof showcase === 'object') {
    const sharedBorderColor = normalizeHexColorString(String(showcase.sharedBorderColor ?? '').trim()) || String(showcase.sharedBorderColor ?? '').trim() || '#f1f5f9';
    const textPanelBgColor = normalizeHexColorString(String(showcase.textPanelBgColor ?? '').trim()) || String(showcase.textPanelBgColor ?? '').trim() || '#ef4444';
    const productPanelBgColor = normalizeHexColorString(String(showcase.productPanelBgColor ?? '').trim()) || String(showcase.productPanelBgColor ?? '').trim() || '#f8fafc';
    const scaleRaw = Number(showcase.sharedFrameScale ?? 1);
    const sharedFrameScale = Number.isFinite(scaleRaw)
      ? Math.min(1, Math.max(0.35, scaleRaw))
      : 1;
    spread.productShowcase = {
      sharedFrameScale,
      panelGapPx: Math.max(0, Number(showcase.panelGapPx ?? 0)),
      sharedBorderWidth: Math.max(0, Number(showcase.sharedBorderWidth ?? 2)),
      sharedBorderRadius: Math.max(0, Number(showcase.sharedBorderRadius ?? 26)),
      sharedBorderColor,
      textPanelBgColor,
      productPanelBgColor,
      // Padding interno dos painéis não é configurável: área útil = painel menos a borda compartilhada.
      textPanelPaddingPx: 0,
      productPanelPaddingPx: 0,
    };
  }
  if (mergedStyles && Object.keys(mergedStyles).length > 0) {
    return { ...spread, styles: mergedStyles };
  }
  const { styles: _omit, ...noStyles } = spread;
  return noStyles;
}

/**
 * Junta dois `panfletoContent` campo a campo (styles / posições).
 * Evita o spread raso `{ ...prev, ...incoming }` apagar `styles.linha1.textAlign`
 * quando o incoming traz `linha1` só com cor/borda/tamanho.
 */
export function mergePanfletoContentPreserving(
  prev?: PanfletoContentConfig | null,
  incoming?: PanfletoContentConfig | null
): PanfletoContentConfig {
  return mergePanfletoContent({
    ...(prev ?? undefined),
    ...(incoming ?? undefined),
    styles: mergePanfletoStyles(prev?.styles, incoming?.styles),
    elementPlacements: mergePanfletoElementPlacements(
      prev?.elementPlacements,
      incoming?.elementPlacements
    ),
  });
}

function panfletoContentHasFieldStyles(pc?: PanfletoContentConfig | null): boolean {
  const styles = pc?.styles;
  return !!styles && typeof styles === 'object' && Object.keys(styles).length > 0;
}

/**
 * Overlay de save de estilo sobre o JSON da API.
 * Se o incoming traz `styles`, mescla campo a campo e nunca apaga `linha1.textAlign`
 * a menos que venha um left/center/right novo.
 * Se o incoming não traz `styles` (restaurar padrão), grava sem reintroduzir o bloco.
 */
export function overlayPanfletoContentKeepingProductNameAlign(
  previous?: PanfletoContentConfig | null,
  incoming?: PanfletoContentConfig | null
): PanfletoContentConfig {
  if (!incoming) {
    return mergePanfletoContent(previous ?? undefined);
  }
  if (!panfletoContentHasFieldStyles(incoming)) {
    return mergePanfletoContent(incoming);
  }
  return mergePanfletoContentPreserving(previous, incoming);
}

/**
 * Lê `panfletoContent` do objeto raiz de `configuracoes` (chaves da API: camel / Pascal / snake).
 * Mesma semântica que `parsePanfletoContentFromApiConfig` em `bannerSyncService`.
 */
export function readPanfletoContentFromConfigObject(
  config: unknown
): PanfletoContentConfig | undefined {
  if (!config || typeof config !== 'object' || Array.isArray(config)) {
    return undefined;
  }
  const c = config as Record<string, unknown>;
  const raw = c.panfletoContent ?? c.PanfletoContent ?? c.panfleto_content;
  if (raw == null) {
    return undefined;
  }
  let o: Record<string, unknown>;
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw.trim());
      if (p == null || typeof p !== 'object' || Array.isArray(p)) {
        return undefined;
      }
      o = { ...(p as object) };
    } catch {
      return undefined;
    }
  } else if (typeof raw === 'object' && !Array.isArray(raw)) {
    o = { ...(raw as object) };
  } else {
    return undefined;
  }
  if (o.backgroundColor === undefined && o.BackgroundColor !== undefined) {
    o.backgroundColor = o.BackgroundColor;
  }
  if (o.styles === undefined && o.Styles != null && typeof o.Styles === 'object') {
    o.styles = o.Styles;
  }
  delete o.tipoQuantidade;
  delete o.TipoQuantidade;
  return o as PanfletoContentConfig;
}

/** Cópia segura de panfletoContent (inclui objetos aninhados em styles) para undo/deepCopy. */
export function clonePanfletoContentDeep(pc: PanfletoContentConfig): PanfletoContentConfig {
  const ep = pc.elementPlacements;
  const { tipoQuantidade: _omitTipo, ...rest } = pc as PanfletoContentConfig & {
    tipoQuantidade?: string;
  };
  return {
    ...rest,
    styles: rest.styles
      ? (Object.fromEntries(
          Object.entries(rest.styles).map(([k, v]) => [k, v && typeof v === 'object' ? { ...v } : v])
        ) as PanfletoContentStyles)
      : undefined,
    elementPlacements: ep
      ? (Object.fromEntries(
          Object.entries(ep).map(([k, v]) => [k, v && typeof v === 'object' ? { ...v } : v])
        ) as PanfletoElementPlacements)
      : undefined,
  };
}

function formatarDataValidadeBr(iso: string | undefined): string {
  if (!iso) return '';
  const s = String(iso).trim();
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split('-');
    return `${d}/${m}/${y}`;
  }
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
    return s;
  }
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString('pt-BR');
  } catch {
    return s;
  }
}

/**
 * Uma linha de texto de validade para o panfleto.
 * Prioridade:
 * 1. Datas digitadas no editor (`validadeDataInicial` / `validadeDataFinal`)
 * 2. `validUntil` / `validFrom` do produto
 * 3. Vazio (não inventa data)
 * Recebe `panfleto` já passado por {@link mergePanfletoContent}.
 */
export function resolveTextoValidadePanfleto(merged: PanfletoContentConfig, promo: Promotion): string {
  const dataInicialDigitada = formatarDataValidadeBr(merged.validadeDataInicial);
  const dataFinalDigitada = formatarDataValidadeBr(merged.validadeDataFinal);

  // 1) Datas do style editor
  if (dataInicialDigitada && dataFinalDigitada) {
    return `Válido de ${dataInicialDigitada}\naté ${dataFinalDigitada}`;
  }
  if (dataInicialDigitada || dataFinalDigitada) {
    return `Válido até ${dataFinalDigitada || dataInicialDigitada}`;
  }

  // 2) Fallback: datas do produto (dtFinalCampanha / dtInicialCampanha)
  const dataFimProduto = formatarDataValidadeBr(promo.validUntil);
  if (!dataFimProduto) return '';
  const dataInicioProduto = formatarDataValidadeBr(promo.validFrom);
  if (dataInicioProduto) {
    return `Válido de ${dataInicioProduto} até ${dataFimProduto}`;
  }
  return `Válido até ${dataFimProduto}`;
}

/** Textos efetivos para exibição: panfletoContent preenchido ou fallback no produto. */
export function resolvePanfletoDisplayTexts(
  content: PanfletoContentConfig | undefined,
  promo: Promotion
) {
  const c = mergePanfletoContent(content);
  const noProduct =
    (!promo.name || !promo.name.trim()) &&
    (!promo.category || !promo.category.trim()) &&
    (!promo.unit || !promo.unit.trim()) &&
    (!promo.validUntil || !promo.validUntil.trim()) &&
    ((promo.price ?? 0) <= 0) &&
    ((promo.originalPrice ?? 0) <= 0);
  const precoPorNum = promo.price;
  const precoDeNum = promo.originalPrice;
  const hasPrecoDeValido =
    !noProduct &&
    precoDeNum !== undefined &&
    !Number.isNaN(precoDeNum) &&
    precoPorNum !== undefined &&
    !Number.isNaN(precoPorNum) &&
    precoDeNum > precoPorNum;
  const formatNum = (n: number | undefined) =>
    n === undefined || Number.isNaN(n)
      ? ''
      : n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return {
    texto1: (c.texto1 ?? '').trim() || '',
    texto2: (c.texto2 ?? '').trim() || '',
    texto3: (c.texto3 ?? '').trim() || '',
    /** Sempre do produto; não vem de panfletoContent. */
    linha1: noProduct ? '' : (promo.name || '').trim() || '',
    linha2: noProduct ? '' : (promo.category || '').trim() || '',
    /** Sempre do produto; só mostra "DE" quando preço normal > promocional. */
    precoDe: hasPrecoDeValido ? formatNum(precoDeNum) : '',
    /** Sempre do produto. */
    precoPor: noProduct ? '' : formatNum(precoPorNum),
    validadeTexto: resolveTextoValidadePanfleto(c, promo),
    /** Padrão de exibição quando o produto não tiver quantidade no cadastro. */
    quantidade: noProduct ? '' : '1',
    unidadeQuantidade: noProduct ? '' : (promo.unit || '').trim(),
  };
}
